import { API_CONFIG, createApiUrl } from '../api'
import type {
  UserProfileResponse,
  UserProfileUpdateRequest,
  UserStatisticsResponse,
  BiddingEligibilityResponse
} from '../../types/types.user'

export class UserAPI {
  
  /**
   * Get user profile by user ID
   */
  static async getUserProfile(userId: string, accessToken: string): Promise<UserProfileResponse> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.USER.PROFILE.replace('{userId}', userId)
    )
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('User not found')
      }
      if (response.status === 403) {
        throw new Error('Access denied')
      }
      throw new Error(`Failed to fetch user profile: ${response.statusText}`)
    }
    
    return response.json()
  }
  
  /**
   * Update user profile
   */
  static async updateProfile(
    userId: string, 
    profileData: UserProfileUpdateRequest,
    accessToken: string
  ): Promise<UserProfileResponse> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.USER.UPDATE_PROFILE.replace('{userId}', userId)
    )
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(profileData),
    })
    
    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to update profile: ${error}`)
    }
    
    return response.json()
  }
  
  /**
   * Update user email
   */
  static async updateEmail(
    userId: string, 
    newEmail: string,
    accessToken: string
  ): Promise<UserProfileResponse> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.USER.UPDATE_EMAIL.replace('{userId}', userId)
    )
    
    const params = new URLSearchParams()
    params.append('newEmail', newEmail)
    
    const response = await fetch(`${url}?${params.toString()}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    })
    
    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to update email: ${error}`)
    }
    
    return response.json()
  }
  
  /**
   * Update user password
   */
  static async updatePassword(
    userId: string,
    newPassword: string,
    accessToken: string
  ): Promise<void> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.USER.UPDATE_PASSWORD.replace('{userId}', userId)
    )

    const params = new URLSearchParams()
    params.append('newPassword', newPassword)

    const response = await fetch(`${url}?${params.toString()}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to update password: ${error}`)
    }
  }

  /**
   * Get user statistics
   */
  static async getUserStatistics(userId: string, accessToken: string): Promise<UserStatisticsResponse> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.USER.STATISTICS.replace('{userId}', userId)
    )

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('User not found')
      }
      if (response.status === 403) {
        throw new Error('Access denied')
      }
      throw new Error(`Failed to fetch user statistics: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Check bidding eligibility
   */
  static async getBiddingEligibility(userId: string, accessToken: string): Promise<BiddingEligibilityResponse> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.USER.BIDDING_ELIGIBILITY.replace('{userId}', userId)
    )

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('User not found')
      }
      if (response.status === 403) {
        throw new Error('Access denied')
      }
      throw new Error(`Failed to fetch bidding eligibility: ${response.statusText}`)
    }

    return response.json()
  }

  /**
   * Send phone verification code
   */
  static async sendPhoneVerificationCode(
    userId: string,
    countryCode: string,
    phoneNumber: string,
    accessToken: string
  ): Promise<void> {
    const url = createApiUrl(`/api/v1/users/${userId}/phone/send-code`)

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ countryCode, phoneNumber }),
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to send verification code: ${error}`)
    }
  }

  /**
   * Verify phone code
   */
  static async verifyPhoneCode(
    userId: string,
    code: string,
    countryCode: string,
    phoneNumber: string,
    accessToken: string
  ): Promise<BiddingEligibilityResponse> {
    const url = createApiUrl(`/api/v1/users/${userId}/phone/verify-code`)

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ code, countryCode, phoneNumber }),
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to verify phone code: ${error}`)
    }

    return response.json()
  }

  /**
   * Send email verification (resend)
   */
  static async sendEmailVerification(
    email: string
  ): Promise<void> {
    const url = createApiUrl('/api/auth/resend-verification')

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Failed to send email verification: ${error}`)
    }
  }

  /**
   * Delete user account (soft delete - anonymizes user data)
   */
  static async deleteAccount(
    userId: string,
    accessToken: string
  ): Promise<void> {
    const url = createApiUrl(
      API_CONFIG.ENDPOINTS.USER.DELETE_ACCOUNT.replace('{userId}', userId)
    )

    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Access denied')
      }
      if (response.status === 404) {
        throw new Error('User not found')
      }
      const error = await response.text()
      throw new Error(`Failed to delete account: ${error}`)
    }
  }
}